import { useEffect } from 'react'
import { useTranslation } from 'next-i18next'
import html from 'html-react-parser'
import Head from 'next/head'

import { css } from '@emotion/react'

import { useRouter } from 'next/router'

import { Grid, mediaColumns } from '@/styles/grid'
import { Text1, Text2, Link1 } from '@/styles/typography'
import { mediaUp } from '@/styles/media'

import { useMarket } from '@/utils/multi-markets/context'
import { DOCUMENTS } from '@/constants'

import { DEFAULT_COLORS } from '@/constants'
import { setCssVars } from '@/utils/common'
import { useFetchCustomizedClients } from '@/service/apiHooks/useFetchCustomizedClients'

import Link from './common/Link'
import LangLink from './common/LangLink'

const AeCopyrightsText = <>{html('NaviParking DMCC')}</>
const BhCopyrightsText = <>{html('Naviparking MENA W.L.L')}</>
const PlCopyrightsText = <>{html('Naviparking Poland Sp. z o.o.')}</>

interface Props {
  short?: boolean
}

const components = {
  bh: BhCopyrightsText,
  ae: AeCopyrightsText,
  pl: PlCopyrightsText
}

function FooterMobile(props: Props) {
  const { t } = useTranslation('common')
  const { short = false } = props
  const footerDate = new Date()
  const { locale, market } = useMarket()
  const CopyrightsText = components[market]
  const router = useRouter()
  const { id } = router.query as any
  const { clientDetails } = useFetchCustomizedClients({
    id
  })

  useEffect(() => {
    const colors = clientDetails?.colorScheme
      ? clientDetails?.colorScheme
      : DEFAULT_COLORS

    if (colors) {
      setCssVars(colors)
    }
  }, [])

  return (
    <>
      <Head>
        <script
          id="CookieConsent"
          src="https://policy.app.cookieinformation.com/uc.js"
          data-culture={locale?.toUpperCase()}
          data-gcm-version="2.0"
          type="text/javascript"
          data-default-consent="true"
          async
        />
      </Head>
      <footer css={container} data-test-id="footer-newsletter">
        {!short && (
          <div css={navigationContainer}>
            <div css={navigationGroup}>
              <div>
                <div data-test-id="footer-portal">
                  <Text1 as="strong">{t('footer.portal')}</Text1>
                  <ul css={linksList}>
                    <li>
                      <Link
                        href={{ pathname: '/parking-search' }}
                        passHref
                        prefetch={false}
                        isCustomized
                        legacy={false}
                      >
                        {t('navigation.search')}
                      </Link>
                    </li>

                    <li>
                      <Link
                        href={{
                          pathname: '/parking-search',
                          query: {
                            service: 'PARK_AND_FLY'
                          }
                        }}
                        passHref
                        prefetch={false}
                        isCustomized
                        legacy={false}
                      >
                        {t('navigation.searchFly')}
                      </Link>
                    </li>

                    <li>
                      <Link
                        href={{ pathname: '/navipay' }}
                        passHref
                        prefetch={false}
                        isCustomized
                        legacy={false}
                      >
                        {t('navigation.navipay')}
                      </Link>
                    </li>

                    <li>
                      <Link
                        href={{ pathname: '/contact' }}
                        passHref
                        prefetch={false}
                        isCustomized
                        legacy={false}
                      >
                        {t('navigation.contact')}
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
              <div>
                <div data-test-id="footer-portal">
                  <div css={placeholderTitle} aria-hidden="true" />
                  <ul css={linksList}>
                    <li>
                      <Link
                        href={{ pathname: '/faq' }}
                        passHref
                        prefetch={false}
                        isCustomized
                        legacy={false}
                      >
                        {t('navigation.faq')}
                      </Link>
                    </li>

                    <li>
                      <Link
                        href={{ pathname: '/case-studies' }}
                        passHref
                        prefetch={false}
                        isCustomized
                        legacy={false}
                      >
                        {t('navigation.landings')}
                      </Link>
                    </li>

                    <li>
                      <Link
                        href={`https://blog.digitalparking.city/${
                          locale === 'pl' ? locale : ''
                        }`}
                        isCustomized
                        legacy={false}
                      >
                        {t('navigation.blog')}
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div css={gap} />
            <div css={navigationGroup}>
              {' '}
              <div>
                <div data-test-id="footer-community">
                  <Text1 as="strong">{t('footer.community')}</Text1>
                  <ul css={linksList}>
                    <li>
                      <Link1
                        href={t('socials.facebook')}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t('footer.facebook')}
                      </Link1>
                    </li>

                    <li>
                      <Link1
                        href={t('socials.linkedin')}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t('footer.linkedin')}
                      </Link1>
                    </li>

                    <li>
                      <Link1
                        href={
                          market === 'pl'
                            ? t('socials.instagram')
                            : 'https://www.instagram.com/naviparking/'
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t('footer.instagram')}
                      </Link1>
                    </li>
                  </ul>
                </div>
              </div>
              <div>
                <div data-test-id="footer-terms">
                  <Text1 as="strong">{t('footer.terms')}</Text1>
                  <ul css={linksList}>
                    <li>
                      <Link1
                        target="_blank"
                        href={DOCUMENTS.terms[market][locale]}
                      >
                        {t('footer.termsOfUse')}
                      </Link1>
                    </li>

                    <li>
                      <Link1
                        target="_blank"
                        href={DOCUMENTS.privacyPolicy[market][locale]}
                      >
                        {t('footer.privacyPolicy')}
                      </Link1>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
        <Grid>
          {router?.pathname?.includes('legia') && (
            <div
              css={copyrightsContainer}
              style={{ marginBottom: 0, marginTop: 20 }}
            >
              <Link1
                href={
                  'https://cdn.naviparking.com/docs/regulamin-konkursu-legia.docx.pdf'
                }
              >
                <Text2 css={copyrightsText}>Regulamin Konkursu Legia</Text2>
              </Link1>
            </div>
          )}
          <div css={divider} />
          <div css={copyrightsContainer}>
            <Text2 css={copyrightsText}>
              © {footerDate.getFullYear()} {CopyrightsText}
            </Text2>
            <LangLink showOnMobileNavbar short />
          </div>
        </Grid>
      </footer>
    </>
  )
}

const container = css`
  flex-shrink: 0;
  z-index: 99;
  background-color: var(--tint);
  html[lang^='ar'] & {
    padding-right: 20px;
  }
`

const divider = css`
  ${mediaColumns({ xs: [12, 0] })};

  height: 2px;
  width: 100%;
  background-color: var(--main);

  opacity: 0.08;
  margin-top: 40px;
`

const navigationContainer = css`
  margin-top: 24px;
  margin-left: 20px;
  position: relative;
`

const navigationGroup = css`
  display: flex;

  > div:first-of-type {
    width: 110px;
  }

  > div:nth-of-type(2) {
    position: absolute;
    left: 200px;
  }
`

const gap = css`
  height: 20px;
`

const placeholderTitle = css`
  display: inline-flex;
  height: 1em;
  line-height: 1.5;
  visibility: hidden;

  ${mediaUp('lg')} {
    margin-bottom: 28px;
  }
`

const linksList = css`
  list-style: none;
  margin-top: 12px;

  li:not(:last-child) {
    margin-bottom: 4px;
  }

  a {
    display: inline-block;
    padding: 4px 0;
  }
`

const copyrightsContainer = css`
  ${mediaColumns({ xs: [12, 0] })};
  margin-bottom: 40px;
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
`

const copyrightsText = css`
  color: var(--main);
`

export default FooterMobile
